import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { empGroupApi, F90T, MemberRequest } from '../../../services/empGroupApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useEmpGroupForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90T[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<F90T | null>(null);

  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const [members, setMembers] = useState<MemberRequest[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await empGroupApi.list(1, 500);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (code: string) => {
    try {
      const d = await empGroupApi.get(code);
      setDetail(d);
      return d;
    } catch { return null; }
  }, []);

  const syncFromDetail = useCallback((d: F90T) => {
    setCode(d.f90t_01);
    setDesc(d.f90t_11);
    setMembers((d.members || []).map(m => ({ f90t1_11: m.f90t1_11, f90t1_11a: m.f90t1_11a })));
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      loadDetail(current.f90t_01).then(d => { if (d) syncFromDetail(d); });
    }
  }, [current, mode, loadDetail, syncFromDetail]);

  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setCode(''); setDesc(''); setMembers([]);
  };

  const startEdit = () => {
    if (!detail) return;
    setMode('edit');
    syncFromDetail(detail);
  };

  const cancel = () => {
    setMode('browse');
    if (detail) syncFromDetail(detail);
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await empGroupApi.create({ f90t_01: code, f90t_11: desc, members });
        message.success('新增成功');
      } else {
        await empGroupApi.update(current!.f90t_01, { f90t_11: desc, members });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f90t_01;
      const idx = data.findIndex((r: F90T) => r.f90t_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await empGroupApi.copy({ new_code: newCode, from_code: current.f90t_01 });
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex((r: F90T) => r.f90t_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '複製失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await empGroupApi.delete(current.f90t_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (text: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f90t_01 >= text);
    if (idx >= 0) setCurrentIdx(idx);
  };

  const addMember = () => {
    if (members.length >= 99) return;
    setMembers([...members, { f90t1_11: '', f90t1_11a: '' }]);
  };

  const removeMember = (idx: number) => {
    setMembers(members.filter((_, i) => i !== idx));
  };

  const updateMember = (idx: number, field: keyof MemberRequest, value: string) => {
    const next = [...members];
    next[idx] = { ...next[idx], [field]: value };
    setMembers(next);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx, detail,
    code, setCode, desc, setDesc, members,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doCopy, doDelete, quickSearch,
    addMember, removeMember, updateMember,
  };
}
